/// <amd-module name="TFS/Admin/Common/Controls/RenameDialog/RenameDialog" />
import React = require("react");

import AdminResources = require("TFS/Admin/Common/Controls/Resources");
import { VssComponent } from "VSS/Platform/Layout";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { Dialog } from "VSSUI/Dialog";
import { format } from "VSS/Core/Util/String";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { TextField } from "VSSUI/TextField";
import { Checkbox } from "VSSUI/Checkbox";
import { ObservableValue } from "VSS/Core/Observable";
import { ContentSize } from "VSSUI/Callout";

import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { IRenameDialogProps } from "TFS/Admin/Common/Controls/RenameDialog/RenameDialog.Props";

interface IRenameDialogState {
    confirmChecked: ObservableValue<boolean>;
    isValid: boolean;
}

export class RenameDialog extends VssComponent<IRenameDialogProps, IRenameDialogState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private newName: ObservableValue<string>;

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationProjects, Feature.OrganizationProjects, action, properties);
    };

    public constructor(props: IRenameDialogProps, context?: any) {
        super(props, context);

        this.state = {
            confirmChecked: new ObservableValue<boolean>(false),
            isValid: false
        };

        this.newName = new ObservableValue<string>("");
    }

    public render(): JSX.Element {
        let errorMessage = this._getErrorMessage();

        return (
            <Dialog
                onDismiss={() => {
                    this.props.onCancel();
                }}
                contentSize={ContentSize.Large}
                className="rename-dialog"
                titleProps={{
                    text: this.props.renameDialogTitle,
                    className: "fontWeightSemiBold"
                }}
                footerButtonProps={[
                    {
                        text: AdminResources.Cancel,
                        onClick: () => {
                            this.props.onCancel();
                        }
                    },
                    {
                        disabled: !this.state.confirmChecked.value || !this.state.isValid,
                        text: AdminResources.RenameButton,
                        onClick: this._onRenameDialogRenameClick,
                        primary: true
                    }
                ]}
            >
                <div className="flex-column">
                    <div id="rename-primary-paragraph">{this.props.renameDialog1}</div>
                    <FormatComponent elementType="div" className="rename-second-paragraph" format={this.props.renameDialog2}>
                        <span className="fontWeightSemiBold">{this.props.url}</span>
                        <span>
                            <Link target="_blank" rel="noopener noreferrer" href={this.props.renameDialog2Link}>
                                {AdminResources.LearnMore}
                            </Link>
                        </span>
                    </FormatComponent>
                    <div className="rename-current-name">
                        <span className="fontWeightSemiBold">{AdminResources.CurrentName}</span> {this.props.name}
                    </div>
                    <FormItem
                        className="rename-textfield"
                        label={<span className="fontWeightSemiBold">{this.props.renameNewName}</span>}
                        message={errorMessage}
                        error={errorMessage !== ""}
                    >
                        <TextField
                            value={this.newName}
                            onChange={this._onNameChanged}
                            placeholder={this.props.newName}
                            ariaLabel={AdminResources.AriaRenameText}
                        />
                    </FormItem>
                    <Checkbox
                        label={this.props.renameConfirmationText}
                        checked={this.state.confirmChecked}
                        className="confirm"
                        onChange={this._onChecked}
                    />
                </div>
            </Dialog>
        );
    }

    private _onNameChanged = (event: any, value: string) => {
        this.newName.value = value;
        this._validateProjectName();
    };

    private _getErrorMessage = (): string => {
        if (this.newName.value !== "" && !this.props.validateName(this.newName.value)) {
            // Error message from the validation
            return format(this.props.invalidNameMessage, this.newName.value);
        } else if (this.newName.value !== "" && this.props.newName && this.props.newName !== this.newName.value) {
            return format(AdminResources.NewNameMessage, this.props.newName);
        } else {
            return "";
        }
    };
    private _validateProjectName = (): void => {
        if (!this.props.validateName(this.newName.value) || (this.props.newName && this.props.newName !== this.newName.value)) {
            this.setState({ isValid: false });
        } else {
            this.setState({ isValid: true });
        }
    };

    private _onRenameDialogRenameClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        const projectName = this.newName && this.newName.value;
        if (projectName) {
            this.props.onRename(projectName);
        }
    };

    private _onChecked = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => {
        this.setState({ confirmChecked: new ObservableValue<boolean>(checked) });
    };
}

VssComponent.register("ms.vss-admin-web.rename-dialog", RenameDialog);
